import React, { useState, useEffect, useMemo, useRef } from "react";
import Picker from "react-mobile-picker";

interface MobileDateWheelProps {
  value?: string;
  onChange: (date: string) => void;
  minAge?: number;
  maxAge?: number;
}

export const MobileDateWheel: React.FC<MobileDateWheelProps> = ({
  value,
  onChange,
  minAge = 16,
  maxAge = 120,
}) => {
  const currentYear = new Date().getFullYear();
  const minYear = currentYear - maxAge;
  const maxYear = currentYear - minAge;

  const months = useMemo(
    () => [
      "Jan", "Feb", "Mar", "Apr", "May", "Jun",
      "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"
    ],
    []
  );

  const getDaysInMonth = (month: number, year: number) => {
    return new Date(year, month, 0).getDate();
  };

  // Parse initial date or use default (max allowed age = minAge years ago)
  const initialDate = value ? new Date(value) : new Date(maxYear, 0, 1);
  const initialMonth = months[initialDate.getMonth()];
  const initialDay = initialDate.getDate().toString();
  const initialYear = initialDate.getFullYear().toString();

  const [pickerValue, setPickerValue] = useState({
    month: initialMonth,
    day: initialDay,
    year: initialYear,
  });

  // Use ref to store onChange callback to avoid dependency issues
  const onChangeRef = useRef(onChange);
  useEffect(() => {
    onChangeRef.current = onChange;
  }, [onChange]);

  // Generate options
  const monthsData = useMemo(() => months, [months]);
  
  const yearsData = useMemo(
    () => Array.from({ length: maxYear - minYear + 1 }, (_, i) => (maxYear - i).toString()),
    [minYear, maxYear]
  );

  const daysData = useMemo(() => {
    const monthIndex = months.indexOf(pickerValue.month);
    const year = parseInt(pickerValue.year);
    const daysInMonth = getDaysInMonth(monthIndex + 1, year);
    return Array.from({ length: daysInMonth }, (_, i) => (i + 1).toString());
  }, [pickerValue.month, pickerValue.year, months]);

  // Update parent when picker value changes
  useEffect(() => {
    const monthIndex = months.indexOf(pickerValue.month);
    const day = parseInt(pickerValue.day);
    const year = parseInt(pickerValue.year);
    
    // Validate day for current month/year
    const maxDays = getDaysInMonth(monthIndex + 1, year);
    const validDay = Math.min(day, maxDays);
    
    // Format date as YYYY-MM-DD without timezone conversion
    const monthStr = String(monthIndex + 1).padStart(2, '0');
    const dayStr = String(validDay).padStart(2, '0');
    const dateString = `${year}-${monthStr}-${dayStr}`;
    
    // Only call onChange if the date actually changed
    if (dateString !== value) {
      onChangeRef.current(dateString);
    }
  }, [pickerValue, months, value]);

  // Adjust day if it exceeds the max days in the selected month
  useEffect(() => {
    const currentDay = parseInt(pickerValue.day);
    const maxDays = daysData.length;
    
    if (currentDay > maxDays) {
      setPickerValue(prev => ({
        ...prev,
        day: maxDays.toString()
      }));
    }
  }, [daysData, pickerValue.day]);

  return (
    <div className="bg-background rounded-lg border border-border overflow-hidden">
      <Picker
        value={pickerValue}
        onChange={(value) => setPickerValue(value as { month: string; day: string; year: string })}
        wheelMode="natural"
        height={216}
        itemHeight={36}
      >
        <Picker.Column name="month">
          {monthsData.map((month) => (
            <Picker.Item key={month} value={month}>
              {({ selected }) => (
                <div
                  className={`flex items-center justify-center transition-all duration-200 ${
                    selected
                      ? "text-foreground font-semibold scale-105"
                      : "text-muted-foreground"
                  }`}
                  style={{ height: 36 }}
                >
                  {month}
                </div>
              )}
            </Picker.Item>
          ))}
        </Picker.Column>
        
        <Picker.Column name="day">
          {daysData.map((day) => (
            <Picker.Item key={day} value={day}>
              {({ selected }) => (
                <div
                  className={`flex items-center justify-center transition-all duration-200 ${
                    selected
                      ? "text-foreground font-semibold scale-105"
                      : "text-muted-foreground"
                  }`}
                  style={{ height: 36 }}
                >
                  {day.padStart(2, '0')}
                </div>
              )}
            </Picker.Item>
          ))}
        </Picker.Column>

        <Picker.Column name="year">
          {yearsData.map((year) => (
            <Picker.Item key={year} value={year}>
              {({ selected }) => (
                <div
                  className={`flex items-center justify-center transition-all duration-200 ${
                    selected
                      ? "text-foreground font-semibold scale-105"
                      : "text-muted-foreground"
                  }`}
                  style={{ height: 36 }}
                >
                  {year}
                </div>
              )}
            </Picker.Item>
          ))}
        </Picker.Column>
      </Picker>
    </div>
  );
};
